import React, { useState } from 'react';
import { X, Lock, Crown, Star, Zap } from 'lucide-react';
import { useTheme } from '../arrangement/ThemeContext';
const BackgroundColorSelector = ({ 
  isOpen, 
  onClose, 
  currentLevel = 1, 
  selectedColor = null, 
  onColorSelect 
}) => {
  const [hoveredColor, setHoveredColor] = useState(null);
  const { colors } = useTheme();

  // Define color tiers based on levels
  const colorTiers = {
    1: {
      name: "Rising Star",
      icon: <span style={{ fontSize: '16px' }}>🌱</span>,
      minLevel: 1,
      colors: [
        { id: 'green-1', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', name: 'Forest Green' },
        { id: 'green-2', gradient: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)', name: 'Mint Green' },
        { id: 'teal-1', gradient: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)', name: 'Ocean Teal' },
        { id: 'teal-2', gradient: 'linear-gradient(135deg, #2dd4bf 0%, #14b8a6 100%)', name: 'Aqua Teal' }
      ]
    },
    25: {
      name: "Enthusiast",
      icon: <span style={{ fontSize: '16px' }}>🍃</span>,
      minLevel: 25,
      colors: [
        { id: 'blue-1', gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', name: 'Sky Blue' },
        { id: 'blue-2', gradient: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)', name: 'Ocean Blue' },
        { id: 'purple-1', gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', name: 'Royal Purple' },
        { id: 'purple-2', gradient: 'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)', name: 'Lavender Purple' }
      ]
    },
    50: {
      name: "Community Champion",
      icon: <Star size={16} style={{ color: '#fbbf24' }} />,
      minLevel: 50,
      colors: [
        { id: 'orange-1', gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', name: 'Sunset Orange' },
        { id: 'orange-2', gradient: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)', name: 'Tangerine' },
        { id: 'pink-1', gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)', name: 'Hot Pink' },
        { id: 'pink-2', gradient: 'linear-gradient(135deg, #f472b6 0%, #ec4899 100%)', name: 'Rose Pink' }
      ]
    },
    75: {
      name: "Diamond Kush",
      icon: <Zap size={16} style={{ color: '#8b5cf6' }} />,
      minLevel: 75,
      colors: [
        { id: 'gold-1', gradient: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)', name: 'Golden Hour' },
        { id: 'gold-2', gradient: 'linear-gradient(135deg, #fcd34d 0%, #fbbf24 100%)', name: 'Champagne Gold' },
        { id: 'red-1', gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', name: 'Ruby Red' },
        { id: 'red-2', gradient: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)', name: 'Crimson Red' }
      ]
    },
    100: {
      name: "Cannabis Legend",
      icon: <Crown size={16} style={{ color: '#ffd700' }} />,
      minLevel: 100,
      colors: [
        { id: 'cosmic-1', gradient: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #7c3aed 100%)', name: 'Cosmic Purple' },
        { id: 'cosmic-2', gradient: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0ea5e9 100%)', name: 'Deep Space' },
        { id: 'rainbow-1', gradient: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 25%, #3b82f6 50%, #10b981 75%, #fbbf24 100%)', name: 'Aurora' },
        { id: 'rainbow-2', gradient: 'linear-gradient(135deg, #fbbf24 0%, #f97316 25%, #ec4899 50%, #8b5cf6 75%, #3b82f6 100%)', name: 'Prismatic' }
      ]
    }
  };

  const getUnlockedTiers = () => {
    return Object.entries(colorTiers).filter(([level]) => currentLevel >= parseInt(level));
  };

  const isColorUnlocked = (tierLevel) => {
    return currentLevel >= tierLevel;
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(10px)'
    }}>
      <div style={{
        background: colors.bgTertiary,
        borderRadius: '24px',
        padding: '32px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        overflowY: 'auto',
        border: '1px solid #333',
        color: '#e5e5e5'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '24px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Choose Background
            </h2>
            <p style={{
              margin: '4px 0 0 0',
              fontSize: '14px',
              color: '#888'
            }}>
              Level {currentLevel} • Unlock more colors as you level up!
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '8px'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Color Tiers */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          {Object.entries(colorTiers).map(([tierLevel, tier]) => {
            const unlocked = isColorUnlocked(parseInt(tierLevel));
            
            return (
              <div key={tierLevel} style={{
                opacity: unlocked ? 1 : 0.4,
                transition: 'opacity 0.3s ease'
              }}>
                {/* Tier Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px',
                  padding: '12px 16px',
                  background: unlocked ? 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)' : '#1f1f1f',
                  borderRadius: '12px',
                  border: unlocked ? '1px solid #00d4aa40' : '1px solid #333'
                }}>
                  {tier.icon}
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: '16px',
                      fontWeight: '600',
                      color: unlocked ? '#e5e5e5' : '#666'
                    }}>
                      {tier.name}
                    </h3>
                    <p style={{
                      margin: 0,
                      fontSize: '12px',
                      color: unlocked ? '#888' : '#555'
                    }}>
                      Level {tier.minLevel}+
                    </p>
                  </div>
                  {!unlocked && (
                    <Lock size={16} style={{ color: '#666' }} />
                  )}
                </div>

                {/* Color Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '12px'
                }}>
                  {tier.colors.map((color) => (
                    <button
                      key={color.id}
                      disabled={!unlocked}
                      onClick={() => unlocked && onColorSelect(color)}
                      onMouseEnter={() => setHoveredColor(color.id)}
                      onMouseLeave={() => setHoveredColor(null)}
                      style={{
                        height: '80px',
                        borderRadius: '16px',
                        border: selectedColor?.id === color.id 
                          ? '3px solid #00d4aa' 
                          : hoveredColor === color.id && unlocked
                            ? '3px solid #00d4aa80'
                            : '2px solid #333',
                        background: color.gradient,
                        cursor: unlocked ? 'pointer' : 'not-allowed',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        transform: hoveredColor === color.id && unlocked ? 'scale(1.02)' : 'scale(1)',
                        opacity: unlocked ? 1 : 0.5
                      }}
                    >
                      {/* Color name overlay */}
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'rgba(0, 0, 0, 0.8)',
                        color: 'white',
                        padding: '8px',
                        fontSize: '12px',
                        fontWeight: '500',
                        textAlign: 'center',
                        backdropFilter: 'blur(10px)'
                      }}>
                        {color.name}
                      </div>

                      {/* Selected indicator */}
                      {selectedColor?.id === color.id && (
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          background: '#00d4aa',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '12px'
                        }}>
                          ✓
                        </div>
                      )}

                      {/* Lock overlay for disabled colors */}
                      {!unlocked && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          background: 'rgba(0, 0, 0, 0.8)',
                          borderRadius: '50%',
                          width: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Lock size={16} style={{ color: '#666' }} />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          marginTop: '32px',
          padding: '16px',
          background: colors.bgCard,
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <p style={{
            margin: 0,
            fontSize: '12px',
            color: '#888'
          }}>
            💡 Tip: Stay active in the community to level up and unlock more colors!
          </p>
        </div>
      </div>
    </div>
  );
};

export default BackgroundColorSelector;